import Link, { LinkProps } from "next/link"
import {
  ComponentPropsWithRef,
  MouseEventHandler,
  PropsWithChildren,
} from "react"

import ButtonLoadingSpinner from "./ButtonLoadingSpinner"
import { classNames } from "../../../../lib/classNames"
import { Icon, IconProps, IconSizes } from "../../../Icon"

export const ButtonMode = {
  Primary: "primary",
  Secondary: "secondary",
  Ghost: "ghost",
  Destructive: "destructive",
  // Violet is the Dooey affordance, pending the AI-surface redesign.
  Violet: "violet",
  Link: "link",
  // Tonal is a soft, tinted action selected by `tone` (see ButtonTone). It is
  // designed for use inside informational surfaces such as Banner, not as a
  // standalone CTA — no consumer should default to it. Tonal buttons have no
  // disabled state and the props type forbids `disabled`: when a tonal action
  // isn't currently available, don't render the button until it is.
  Tonal: "tonal",
} as const
export type ButtonMode = (typeof ButtonMode)[keyof typeof ButtonMode]
export type NonTonalButtonMode = Exclude<ButtonMode, typeof ButtonMode.Tonal>

export const ButtonTone = {
  Danger: "danger",
  Alert: "alert",
  Success: "success",
  Information: "information",
  AI: "ai",
} as const
export type ButtonTone = (typeof ButtonTone)[keyof typeof ButtonTone]

export const ButtonSize = {
  Default: "default",
  Small: "small",
  Medium: "medium",
  ExtraSmall: "extraSmall",
} as const
export type ButtonSize = (typeof ButtonSize)[keyof typeof ButtonSize]

export type ToneProps =
  | { mode: typeof ButtonMode.Tonal; tone: ButtonTone; disabled?: never } // tonal: tone required, disabled forbidden
  | { mode: NonTonalButtonMode; tone?: never } // otherwise: tone forbidden

export type BaseButtonProps = ComponentPropsWithRef<"button"> &
  ToneProps & {
    size?: ButtonSize
    selected?: boolean
    loading?: boolean
    leftIcon?: IconProps["icon"]
    rightIcon?: IconProps["icon"]
    enablePropagation?: boolean
  }

export type ButtonProps = BaseButtonProps & ComponentPropsWithRef<"button">

export function Button(props: ButtonProps) {
  const {
    children,
    className,
    mode,
    tone = ButtonTone.Information,
    size = ButtonSize.Default,
    selected,
    loading = false,
    leftIcon,
    rightIcon,
    onClick,
    enablePropagation = false,
    ...otherProps
  } = props

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (!enablePropagation) {
      event.stopPropagation()
    }
    if (loading) return
    onClick?.(event)
  }

  return (
    <button
      type="button"
      className={containerClassName(size, mode, tone, selected, className)}
      aria-pressed={selected}
      onClick={handleClick}
      {...otherProps}
    >
      <ButtonContent
        size={size}
        loading={loading}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
        mode={mode}
        tone={tone}
        selected={selected}
      >
        {children}
      </ButtonContent>
    </button>
  )
}

export type LinkButtonProps = BaseButtonProps &
  LinkProps &
  Omit<ComponentPropsWithRef<"a">, keyof LinkProps>

export function LinkButton(props: LinkButtonProps) {
  const {
    children,
    className,
    mode,
    tone = ButtonTone.Information,
    size = ButtonSize.Default,
    selected,
    loading = false,
    leftIcon,
    rightIcon,
    ...otherProps
  } = props

  return (
    <Link
      className={containerClassName(size, mode, tone, selected, className)}
      {...otherProps}
    >
      <ButtonContent
        size={size}
        loading={loading}
        leftIcon={leftIcon}
        rightIcon={rightIcon}
        mode={mode}
        tone={tone}
        selected={selected}
      >
        {children}
      </ButtonContent>
    </Link>
  )
}

interface ButtonContentProps {
  mode: ButtonMode
  tone: ButtonTone
  size?: ButtonSize
  selected?: boolean
  loading?: boolean
  leftIcon?: IconProps["icon"]
  rightIcon?: IconProps["icon"]
}

function ButtonContent({
  size = ButtonSize.Default,
  loading = false,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  mode,
  tone,
  selected,
  children,
}: PropsWithChildren<ButtonContentProps>) {
  const hasIcon = LeftIcon !== undefined || RightIcon !== undefined
  const iconClasses = classNames(
    mode === ButtonMode.Tonal ? tonalIconClassName[tone] : iconClassName[mode],
    selected && selectedIconClassName
  )

  return (
    <>
      <ButtonLoadingSpinner size={size} loading={loading} />
      <div
        className={classNames(
          loading && "invisible",
          hasIcon && "inline-flex items-center gap-x-1.5"
        )}
      >
        {LeftIcon && (
          <Icon
            size={IconSizes.ExtraSmall}
            icon={LeftIcon}
            className={iconClasses}
          />
        )}
        {children}
        {RightIcon && (
          <Icon
            size={IconSizes.ExtraSmall}
            icon={RightIcon}
            className={iconClasses}
          />
        )}
      </div>
    </>
  )
}

function containerClassName(
  size: ButtonSize,
  mode: ButtonMode,
  tone: ButtonTone,
  selected?: boolean,
  className?: string
) {
  return classNames(
    "text-body group relative inline-flex select-none items-center justify-center whitespace-nowrap rounded-control font-medium disabled:cursor-not-allowed",
    sizeClassName[size],
    mode === ButtonMode.Tonal ? tonalClassName[tone] : modeClassName[mode],
    selected && selectedClassName,
    className
  )
}

const selectedClassName = classNames(
  "bg-v2-background-active hover:not-disabled:bg-v2-background-active active:not-disabled:bg-v2-background-active disabled:bg-v2-background-surface-disabled",
  "text-v2-foreground-active hover:not-disabled:text-v2-foreground-active disabled:text-v2-foreground-default-disabled",
  "border border-v2-border-active hover:not-disabled:border-v2-border-active active:not-disabled:border-v2-border-active disabled:border-v2-border-default",
  "shadow-none hover:not-disabled:shadow-none active:not-disabled:shadow-none disabled:shadow-none"
)

const selectedIconClassName =
  "text-v2-foreground-active group-hover:text-v2-foreground-active group-disabled:text-v2-foreground-default-disabled"

const sizeClassName: Record<ButtonSize, string> = {
  [ButtonSize.Default]: "h-10 px-4",
  [ButtonSize.Small]: "h-7 px-2",
  [ButtonSize.Medium]: "h-8 px-3",
  [ButtonSize.ExtraSmall]: "h-5 px-1",
}

const modeClassName: Record<NonTonalButtonMode, string> = {
  [ButtonMode.Primary]: classNames(
    "bg-v2-background-primary hover:not-disabled:bg-v2-background-primary-strong active:not-disabled:bg-v2-background-primary-stronger disabled:bg-v2-background-primary-disabled",
    "text-v2-foreground-inverse disabled:text-v2-foreground-inverse-disabled"
  ),
  [ButtonMode.Secondary]: classNames(
    "bg-v2-background-surface hover:not-disabled:bg-v2-background-surface-strong active:not-disabled:bg-v2-background-surface-stronger disabled:bg-v2-background-surface-disabled",
    "text-v2-foreground-default disabled:text-v2-foreground-default-disabled",
    "border border-v2-border-default hover:not-disabled:border-v2-border-strong active:not-disabled:border-v2-border-strong disabled:border-v2-border-default",
    "shadow-v2-button active:not-disabled:shadow-none disabled:shadow-none"
  ),
  [ButtonMode.Ghost]: classNames(
    "bg-transparent hover:not-disabled:bg-v2-background-surface-strong active:not-disabled:bg-v2-background-surface-stronger disabled:bg-v2-background-surface-disabled",
    "text-v2-foreground-default disabled:text-v2-foreground-default-disabled"
  ),
  [ButtonMode.Destructive]: classNames(
    "bg-v2-background-surface hover:not-disabled:bg-v2-background-danger active:not-disabled:bg-v2-background-danger-strong disabled:bg-v2-background-surface-disabled",
    "text-v2-foreground-danger hover:not-disabled:text-v2-foreground-inverse active:not-disabled:text-v2-foreground-inverse disabled:text-v2-foreground-danger-disabled",
    "border border-v2-border-default hover:not-disabled:border-transparent active:not-disabled:border-transparent",
    "shadow-v2-button active:not-disabled:shadow-none disabled:shadow-none"
  ),
  [ButtonMode.Violet]: classNames(
    "bg-accent-violet hover:not-disabled:bg-accent-violet-hovered active:not-disabled:bg-accent-violet-pressed disabled:bg-neutral-default",
    "color-accent-violet disabled:color-button-disabled",
    "border border-v2-border-ai disabled:border-v2-border-default-disabled shadow-[0px_1px_2px_0px_rgba(0,0,0,0.07)]"
  ),
  [ButtonMode.Link]: classNames(
    "bg-transparent underline",
    "text-v2-foreground-primary hover:not-disabled:text-v2-foreground-primary-strong disabled:text-v2-foreground-primary-disabled"
  ),
}

// Tonal palettes are written out per tone (not built from the tone string) so
// Tailwind can see every utility as a literal at build time. There are no
// disabled classes because tonal buttons have no disabled state — ToneProps
// forbids `disabled`; hide the button instead when its action is unavailable.
const tonalClassName: Record<ButtonTone, string> = {
  [ButtonTone.Danger]: classNames(
    "bg-v2-background-danger-tonal hover:bg-v2-background-danger-tonal-strong active:bg-v2-background-danger-tonal-stronger",
    "text-v2-foreground-danger-tonal hover:text-v2-foreground-danger-tonal-strong active:text-v2-foreground-danger-tonal-stronger"
  ),
  [ButtonTone.Alert]: classNames(
    "bg-v2-background-alert-tonal hover:bg-v2-background-alert-tonal-strong active:bg-v2-background-alert-tonal-stronger",
    "text-v2-foreground-alert-tonal hover:text-v2-foreground-alert-tonal-strong active:text-v2-foreground-alert-tonal-stronger"
  ),
  [ButtonTone.Success]: classNames(
    "bg-v2-background-success-tonal hover:bg-v2-background-success-tonal-strong active:bg-v2-background-success-tonal-stronger",
    "text-v2-foreground-success-tonal hover:text-v2-foreground-success-tonal-strong active:text-v2-foreground-success-tonal-stronger"
  ),
  [ButtonTone.Information]: classNames(
    "bg-v2-background-information-tonal hover:bg-v2-background-information-tonal-strong active:bg-v2-background-information-tonal-stronger",
    "text-v2-foreground-information-tonal hover:text-v2-foreground-information-tonal-strong active:text-v2-foreground-information-tonal-stronger"
  ),
  [ButtonTone.AI]: classNames(
    "bg-v2-background-ai-tonal hover:bg-v2-background-ai-tonal-strong active:bg-v2-background-ai-tonal-stronger",
    "text-v2-foreground-ai-tonal hover:text-v2-foreground-ai-tonal-strong active:text-v2-foreground-ai-tonal-stronger"
  ),
}

const iconClassName: Record<NonTonalButtonMode, string> = {
  [ButtonMode.Primary]:
    "text-v2-foreground-inverse group-disabled:text-v2-foreground-inverse-disabled",
  [ButtonMode.Secondary]:
    "text-v2-foreground-default group-disabled:text-v2-foreground-default-disabled",
  [ButtonMode.Ghost]:
    "text-v2-foreground-default group-disabled:text-v2-foreground-default-disabled",
  [ButtonMode.Destructive]:
    "text-v2-foreground-danger group-hover:text-v2-foreground-inverse group-active:text-v2-foreground-inverse group-disabled:text-v2-foreground-danger-disabled",
  [ButtonMode.Violet]:
    "color-icon-accent-violet group-disabled:color-icon-disabled",
  [ButtonMode.Link]:
    "text-v2-foreground-primary group-hover:text-v2-foreground-primary-strong group-disabled:text-v2-foreground-primary-disabled",
}

const tonalIconClassName: Record<ButtonTone, string> = {
  [ButtonTone.Danger]:
    "text-v2-foreground-danger-tonal group-hover:text-v2-foreground-danger-tonal-strong group-active:text-v2-foreground-danger-tonal-stronger",
  [ButtonTone.Alert]:
    "text-v2-foreground-alert-tonal group-hover:text-v2-foreground-alert-tonal-strong group-active:text-v2-foreground-alert-tonal-stronger",
  [ButtonTone.Success]:
    "text-v2-foreground-success-tonal group-hover:text-v2-foreground-success-tonal-strong group-active:text-v2-foreground-success-tonal-stronger",
  [ButtonTone.Information]:
    "text-v2-foreground-information-tonal group-hover:text-v2-foreground-information-tonal-strong group-active:text-v2-foreground-information-tonal-stronger",
  [ButtonTone.AI]:
    "text-v2-foreground-ai-tonal group-hover:text-v2-foreground-ai-tonal-strong group-active:text-v2-foreground-ai-tonal-stronger",
}
